/**
 * Angular imports.
 */
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, OnInit, OnDestroy } from '@angular/core';
/**
 * Model imports.
 */
import { Community } from '../../onboarding/interface/global.model';
import { AnalyticsInterface } from '../../analytics-event/analytics.model';
/**
 * Constant imports
 */
import { COMMUNITY_USER_TYPE,
  NUMBERS,
  COMMUNITY_PRIVACY,
  COMMUNITY_TYPE,
  COMMUNITY_JOIN_TYPE,
  ERROR_CODES,
  PRODUCT_TYPE_CONSTANT,
  SCREEN_NAME,
  PRODUCT_TYPE_CONSTANT_NAME,
  CTA_TYPE,
  ACTION_TYPE,
  ACTION_ON} from '../../constants/app.constants';
import { EVENT_NAME } from '../../analytics-event/analytics.constant';
/**
 * Import service.
 */
import { FunCollectionService } from '../common-functions/fun-collection.service';
import { NewLanguageService } from './../../dataservices/new-language/new-language.service';
/**
 * RxJs imports.
 */
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

/**
 * sdk imports
 */
import { Analytics } from 'analytics2.0';

/**
 * Show the individual community list.
 */
@Component({
  selector: 'app-community-list',
  templateUrl: './community-list.component.html',
  styleUrls: ['./community-list.component.scss']
})
export class CommunityListComponent implements OnInit, OnDestroy {

  /**
   * @viewChild instance for tooltip text
   */
  @ViewChild('tooltipText') tooltipText: ElementRef;
  /**
   * Show the individual community list.
   * Show the translations key.
   * Check the last element of error.
   * Logged in user's user id.
   */
  @Input() community: Community;
  @Input() translations: { [key: string]: string } = {};
  @Input() isLast: boolean;
  @Input() userId: string;
  @Input() cardView: boolean;

  @Input() callingFrom:string;

  /**
   * Emit the edit, delete, unFollow, report, join public community, reload community list event outside.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() emitCommunityEdit: EventEmitter<any> = new EventEmitter();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() emitCommunityDelete: EventEmitter<any> = new EventEmitter();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() emitCommunityUnFollow: EventEmitter<any> = new EventEmitter();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() emitCommunityReport: EventEmitter<any> = new EventEmitter();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() emitCommunityDetail: EventEmitter<any> = new EventEmitter();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() emitJoinPublicCommunity: EventEmitter<any> = new EventEmitter();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() emitWithdrawRequest: EventEmitter<any> = new EventEmitter();
  @Output() emitReloadCommunityList: EventEmitter<boolean> = new EventEmitter();
  /**
   * Member Type Admin
   * Number Type constant
   * Community privacy constant
   * Loader for ungoing join api requests
   * withdrawRequest
   * communityJoinType
   * Is community active.
  */
  public adminMemberType = COMMUNITY_USER_TYPE.ADMIN;
  public numbers = NUMBERS;
  public communityPrivacy = COMMUNITY_PRIVACY;
  public loader = false;
  public withdrawRequest = false;
  public communityJoinType = COMMUNITY_JOIN_TYPE;
  public isCommunityActive = NUMBERS.ONE;

  /**
   * @notifier$ defines a notifier subject stream to end data streaming.
   */
  private notifier$ = new Subject();

  /**
   * Past community type.
   */
  public pastCommunityType = COMMUNITY_TYPE.PAST;
  /**
   * Session time object.
   */
  public sessiontime = { 'start_time': '', 'end_time': '' };
  /**
   * Analyticsdata object.
   */
  public analyticsData: AnalyticsInterface;
  /**
   * Analytics Done click event.
   */
  public analyticsVisitData: AnalyticsInterface;
  /**
   * Analytics on join click.
   */
  public analyticsJoinData: AnalyticsInterface;

  /**
   * Instance created.
   */
  constructor(
    public fcs: FunCollectionService,
    public mls: NewLanguageService,
    private analyticSdk: Analytics
  ) { }

  ngOnInit(): void {
    if (this.community) {
      this.requestWithdrawStatus();
    }
  }

  /**
   * Emit the edit event outside.
   */
  edit(community): void {
    this.fcs.navigateToTop();
    this.emitCommunityEdit.emit(community);

    const start_time = new Date().getTime().toString();
    this.analyticSdk.analyticService.CLICK(PRODUCT_TYPE_CONSTANT_NAME.COMMUNITY,community.da_id,this.callingFrom, CTA_TYPE.MORE_OPTION, ACTION_TYPE.CLICK,ACTION_ON.EDIT_COMMUNITY, null);
  }

  /**
   * Emit the delete event outside.
   */
  delete(community): void {
    this.emitCommunityDelete.emit(community);
  }

  /**
   * Emit the unFollow event outside.
   */
  unFollow(community): void {
    this.emitCommunityUnFollow.emit(community);
  }

  /**
   * Emit the report event outside.
   */
  report(community): void {
    this.emitCommunityReport.emit(community);
  }
  /**
   * Emit The Community Detail Event Outside
   */
  goToCommunityDetail(community): void {
    this.emitCommunityDetail.emit(community);

    const date = new Date();
    const start_time = date.getTime().toString();
    /**
     * Set analyics data on click.
     */
    if (this.callingFrom === SCREEN_NAME.COMMUNITY_DASHBOARD || this.callingFrom === SCREEN_NAME.PUBLIC_COMMUNITIES_LIST ||
      this.callingFrom === SCREEN_NAME.MY_COMMUNITIES_LIST || this.callingFrom === SCREEN_NAME.PAST_INACTIVE_COMMUNITIES_LIST) {
      this.analyticSdk.analyticService.CLICK(PRODUCT_TYPE_CONSTANT_NAME.COMMUNITY,this.community?.da_id, this.callingFrom, CTA_TYPE.CARD, ACTION_TYPE.CLICK,ACTION_ON.COMMUNITY, null);
    }

  }

  /**
   * Emit Join public community event
   */
  joinCommunityHandler(): void {
    if (this.loader == false) {
      this.analyticSdk.analyticService.CLICK(PRODUCT_TYPE_CONSTANT_NAME.COMMUNITY,this.community?.da_id, this.callingFrom, CTA_TYPE.BUTTON, ACTION_TYPE.CLICK, ACTION_ON.JOIN_COMMUNITY, null);
    }
    this.loader = true;
    this.fcs.joinPublicCommunity(this.community.id).pipe(
      takeUntil(this.notifier$)
    ).subscribe(response => {
      this.loader = false;
      if (response.status === NUMBERS.ONE) {
        this.fcs.showSuccess(this.mls.languageText.toster.key2, this.mls.languageText.toster.key2);
        this.fcs.navigateToCommunityDetailPage(this.community.id);
      } else {
        this.fcs.errorHandeler(response);
      }
    },
    (error) => {
      this.loader = false;
      this.fcs.errorHandeler(error);
    });
  }

  /**
   * Event handler for request to admin to join public community
   */
  requestToJoinHandler(action: string): void {
    if (this.loader == false) {
      this.analyticSdk.analyticService.CLICK(PRODUCT_TYPE_CONSTANT_NAME.COMMUNITY,this.community?.da_id, this.callingFrom, CTA_TYPE.BUTTON, ACTION_TYPE.CLICK, ACTION_ON.COMMUNITY_JOIN_REQUEST, null);
    }
    this.loader = true;
    this.fcs.joinPublicCommunity(this.community.id, action).pipe(
      takeUntil(this.notifier$)
    ).subscribe(response => {
      this.loader = false;
      if (response.status === this.numbers.ONE && response.code === this.numbers.TWO_THOUSAND) {
        this.fcs.showSuccess(this.mls.languageText.community.key89, this.mls.languageText.toster.key2);
        this.withdrawRequest = true;
      }
      if (response.error && response.error.code === ERROR_CODES.ALREADY_EXIST) {
        this.withdrawRequest = false;
        this.fcs.showSuccess(response.error.msg, this.mls.languageText.toster.key2);
        this.emitReloadCommunityList.emit(true);
      }
    });
  }

  /**
   * Join community on conditional base.
   */
  joinCommunity(): void {
    const start_time = new Date().getTime().toString();

    if (this.community?.flags?.is_active === NUMBERS.ZERO) {
      return;
    }

    if (this.withdrawRequest || this.community?.request?.response === this.numbers.TWO) {
      const request = {openModal: this.withdrawRequest || this.community?.request?.response === this.numbers.TWO, community: this.community, callingFrom: this.callingFrom};
      this.analyticSdk.analyticService.CLICK(PRODUCT_TYPE_CONSTANT_NAME.COMMUNITY,this.community?.da_id, this.callingFrom, CTA_TYPE.BUTTON, ACTION_TYPE.CLICK, ACTION_ON.WITHDRAW_REQUEST, null);
      this.emitWithdrawRequest.emit(request);
    } else {
      this.community?.join_type === this.communityJoinType.APPROVAL
      ? this.requestToJoinHandler(this.numbers.ONE.toString())
      : this.joinCommunityHandler();

      if (this.community?.join_type === COMMUNITY_JOIN_TYPE.APPROVAL) {
        /**
         * Set analytics data for analytics event.
         */
        this.analyticSdk.analyticService.CLICK(PRODUCT_TYPE_CONSTANT_NAME.COMMUNITY,this.community?.da_id, this.callingFrom, CTA_TYPE.BUTTON, ACTION_TYPE.CLICK, ACTION_ON.COMMUNITY_JOIN_REQUEST, null);

      } else if (this.community?.join_type === COMMUNITY_JOIN_TYPE.AUTOMATIC) {
        /**
         * Set analytics data for analytics event.
         */
        this.analyticSdk.analyticService.CLICK(PRODUCT_TYPE_CONSTANT_NAME.COMMUNITY,this.community?.da_id, this.callingFrom, CTA_TYPE.BUTTON, ACTION_TYPE.CLICK, ACTION_ON.JOIN_COMMUNITY, null);
      }
    }
  }
  /**
   * Withdraw actions to get on card CTA click.
   */
  requestWithdrawStatus(): void {
    this.fcs.getWithdrawStatus.pipe(
      takeUntil(this.notifier$),
      filter(community => community['community'].id === this.community.id))
      .subscribe(community => {
        this.loader = true;
        if (community) {
            this.fcs.joinPublicCommunity(
              this.community.id,
              this.numbers.ZERO.toString()).pipe(
              takeUntil(this.notifier$)
            ).subscribe(response => {
              this.loader = false;
              if (response.status === this.numbers.ONE && response.code === this.numbers.TWO_THOUSAND) {
                community['community'].request.response = this.numbers.ZERO;
                community['community'].request.type = '';
                this.fcs.showSuccess(this.mls.languageText.community.key90, this.mls.languageText.toster.key2);
                this.withdrawRequest = false;
              }
              if (response.error && response.error.code === ERROR_CODES.ALREADY_EXIST) {
                this.fcs.showSuccess(response.error.msg, this.mls.languageText.toster.key2);
                this.emitReloadCommunityList.emit(true);
              }
            },
            (error) => {
              this.loader = false;
              this.fcs.errorHandeler(error);
            });
        }
      });
  }
  /**
   * Card CTA text.
   */
  get cardActionText(): string {
    return (this.community?.join_type === this.communityJoinType.APPROVAL
      ? (this.withdrawRequest || this.community?.request?.response === this.numbers.TWO ? this.mls.languageText.community.key88 : this.mls.languageText.community.key103)
      : this.mls.languageText.community.key72);
  }

  /**
   * Click event handler for devices
   */
  showTooltip(event: Event): void {
    event.stopPropagation();
    this.tooltipText.nativeElement.classList.remove('hidden');
    this.tooltipText.nativeElement.classList.add('visible');
    setTimeout(() => {
      this.tooltipText.nativeElement.classList.remove('visible');
      this.tooltipText.nativeElement.classList.add('hidden');
    }, 2000);
  }

  /**
   * returns checks to show join community CTA
   */
  get showJoinButton(): boolean {
    return this.community.member_type === '' &&
      this.community.privacy === COMMUNITY_PRIVACY.PUBLIC &&
      this.community.flags.show_join_button === NUMBERS.ONE;
  }

  /**
   * unsubscribes subscriptions
   */
  ngOnDestroy(): void {
    this.notifier$.next();
    this.notifier$.complete();
  }
}
